// HOW MANY VIDEOS A WEEK A CREATOR OWES ONE BRAND — the vocabulary, the default
// and what a legal value is.
//
// Pure, store-free and React-free, for exactly the reason lib/crm-knobs.ts is:
// BOTH SIDES NEED IT AND ONLY ONE OF THEM MAY TOUCH SQLITE. The Schedule page
// writes these numbers from a browser, the Videos page reads one when it mints a
// week, and the Schedule page's server shell renders the control — a definition
// living in the server module would drag better-sqlite3 into every component
// that wanted to know what five means.
//
// Saved weeks always owe what their own plans hold; changing this number never
// resizes them. The daily operations reader also projects not-yet-created weeks
// from this explicit commitment and a confirmed schedule. Those read-only slots
// are replaced by the saved plan as soon as the week exists.
//
// ⚠️ `brand_commitment` IS A WIRE VALUE, and so is the shape of its id. The kind
// is the `kind` column of every row it writes and the id is `creator::brand`;
// there is no migration runner, and renaming either is a data migration nobody
// has a tool for. It is the same key `week_date` and `week_plan` are written
// under (lib/schedule-store.ts's own `weekKey`), one component short — TWO IDS,
// NEVER A DISPLAY NAME, so a creator renaming their Slack profile or an admin
// renaming a brand cannot strand the row behind a key nothing looks up.

import { VIDEOS_PER_WEEK } from "./schedule-dates";
import type { CreatorRef } from "./persona-types";

/**
 * The videos a week nobody has said anything about.
 *
 * ⚠️ IT IS `schedule-dates.ts`'s OWN FIVE, IMPORTED RATHER THAN RE-SPELLED, and
 * that is the whole argument for it being a default at all. The CRM pages' rule
 * is that no figure on a dashboard may be one the app invented; five survives
 * it because it is not invented — it is the SHAPE OF A REAL WEEK in this
 * system, the five publish days a batch has always had. A second literal `5`
 * here would be a number somebody typed, and the argument would stop being true
 * the day one of them moved.
 */
export const DEFAULT_COMMITMENT = VIDEOS_PER_WEEK;

/** The `store` kind. ⚠️ A wire value — see the header. */
export const COMMITMENT_KIND = "brand_commitment";

/** What one row holds. `setBy` is the provenance half; the store's own
 *  `updated_at` is the other, so nothing here has to carry a clock. */
export interface Commitment {
  videosPerWeek: number;
  /** The acting persona who typed it — a human-input number carries a name. */
  setBy: string;
}

/**
 * The row's id: the creator, then the brand.
 *
 * Kept here rather than in the store so the SERVER can spell it the same way
 * without importing a "use client" module. Two spellings of one key is the bug
 * this exists to make impossible.
 */
export const commitmentKey = (creatorEmail: string, brandSlug: string): string =>
  `${creatorEmail}::${brandSlug}`;

/** The two halves back out of a row id, or null for anything else. */
export function parseCommitmentKey(id: string): { creatorEmail: string; brandSlug: string } | null {
  const parts = id.split("::");
  if (parts.length !== 2 || !parts[0] || !parts[1]) return null;
  return { creatorEmail: parts[0], brandSlug: parts[1] };
}

/**
 * The ceiling, and it is a FAT-FINGER GUARD AND NOTHING MORE. The app's own week
 * is five publish days and a batch is one creator's week; there is no contract
 * shape in which one of them holds four times that. Its job is to catch the 400
 * that would otherwise quietly become a week of four hundred slots.
 */
export const MAX_COMMITMENT = 20;

/**
 * The floor, and ZERO IS BELOW IT: zero is not allowed, the minimum is 1.
 *
 * ⚠️ IT WAS ONCE LEGAL AND MEANT "THIS CREATOR IS PAUSED". The reading never
 * worked: a commitment sizes the week it mints, and a week of zero videos is a
 * week that cannot exist — the draft that brings it into being has nowhere to
 * go, which is why `slotsForNewWeek` had to clamp it back up to one and then
 * explain that what they were OWED was nevertheless zero. That explanation died
 * with the resolution chain it depended on. A creator who owes nothing this
 * week is a week nobody creates, which costs nothing to express and needs no
 * special value.
 */
export const MIN_COMMITMENT = 1;

/**
 * What this control would actually STORE for a number somebody typed —
 * canonical, or a throw carrying the sentence to show them.
 *
 * ⚠️ A WHOLE NUMBER, because half a video is not a thing that publishes.
 *
 * ⚠️ AND AT LEAST ONE — see MIN_COMMITMENT. Clearing the field is how you get
 * back to unset (`null`, a tombstone), and it is the only way back; zero is not
 * a quieter spelling of it.
 *
 * Separate from the store's write so a caller can ask BEFORE writing anything:
 * the page shows the refusal beside the field rather than discovering it in a
 * console. The message is written to be read by the person who typed.
 */
export function commitmentValue(raw: string): number {
  const text = raw.trim();
  if (!/^\d{1,3}$/.test(text)) {
    throw new Error("A commitment is a whole number of videos a week.");
  }
  const n = Number(text);
  if (n < MIN_COMMITMENT) {
    throw new Error(
      "A commitment is at least one video a week. To pause a creator, stop creating their weeks — clear the field to go back to the default.",
    );
  }
  if (n > MAX_COMMITMENT) {
    throw new Error(
      `A week holds at most ${MAX_COMMITMENT} videos — the app’s own week is five publish days. Check the number.`,
    );
  }
  return n;
}

/**
 * One stored row, or null for anything this app did not write.
 *
 * ⚠️ IT VALIDATES ON THE WAY OUT, not only on the way in. These rows arrive from
 * `/api/store` as `unknown`, and a row written by an older client — or by hand —
 * that carried a string, a float or a 400 would otherwise become a slot count
 * and then a denominator. A row we cannot read is treated as absent, which falls
 * back to the default rather than to zero: "we cannot read it" and "they owe
 * nothing" are different facts and only one of them is safe to assume.
 */
export function readCommitment(data: unknown): Commitment | null {
  if (!data || typeof data !== "object") return null;
  const raw = (data as { videosPerWeek?: unknown; setBy?: unknown }).videosPerWeek;
  if (typeof raw !== "number" || !Number.isInteger(raw) || raw < MIN_COMMITMENT || raw > MAX_COMMITMENT)
    return null;
  const setBy = (data as { setBy?: unknown }).setBy;
  return { videosPerWeek: raw, setBy: typeof setBy === "string" ? setBy : "" };
}

/**
 * THE SLOTS A NEWLY MINTED WEEK GETS.
 *
 * ⚠️ IT IS THE COMMITMENT, AND THE CLAMP IS NOW ONLY A GUARD. A week is created
 * by minting its first draft — that draft is what makes the week exist at all
 * (newWeekGate reads it, the Videos page renders it, /api/store refuses a
 * brandless one) — so a plan of zero days would be a week with nowhere to put
 * the thing that created it. Since zero went, no legal commitment is below
 * one, so nothing this clamps can arrive through the control; what is left for
 * it to catch is a row somebody wrote by hand, and a week of one is a better
 * answer to that than a week with no grid.
 */
export const slotsForNewWeek = (videosPerWeek: number): number => Math.max(1, videosPerWeek);

// ─── What the Schedule page is handed ───────────────────────────────────────
//
// The shape of the server shell's prop, HERE rather than beside the page,
// because the page that renders it is a client component and the page that
// builds it is a server one — a type declared in either would drag the other's
// import graph across the boundary. Same reason `BrandRef` lives in
// persona-types.ts rather than in the layout that passes it.

/** One creator's line under a brand's calendar. */
export interface RosterCommitment extends CreatorRef {
  /**
   * ⚠️ THE NUMBER SOMEBODY SET, OR NULL — never the resolved one. The field's
   * value must be EMPTY for a creator nobody has set one for, or a default
   * renders as a decision, which is the one reading a control of this kind may
   * never allow.
   */
  videosPerWeek: number | null;
  /** What an unset creator currently falls back to. A placeholder, never a value. */
  inherits: number | null;
  /**
   * WHERE THIS LINE'S NUMBER CAME FROM, as a finished sentence — "6 a week, set
   * by am@yws.com 2 days ago", or what an unset creator is falling back to.
   *
   * ⚠️ BUILT ON THE SERVER, not from a `setAt` passed down and formatted here.
   * "2 days ago" is `Date.now()` minus a stamp, and a client component that
   * computes one during SSR and again during hydration can disagree with itself
   * across midnight — a hydration mismatch in a tooltip, which is the least
   * debuggable place for one.
   */
  note: string;
}

/** A brand, and the creators staffed onto it. */
export interface BrandRoster {
  slug: string;
  creators: RosterCommitment[];
}
